import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const FIELDS = [
  { key: "health_allergies", label: "Allergies relevant to training" },
  { key: "health_medication", label: "Medication considerations for emergency response" },
  { key: "health_conditions", label: "Medical conditions relevant to travel safety" },
  { key: "health_emergency_action", label: "Emergency action information" },
  { key: "health_mobility", label: "Mobility (health-related)" },
  { key: "health_other", label: "Other essential safety information" },
];

export default function HealthTab({ child, reload }) {
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission("children:update");
  const [form, setForm] = useState(() =>
    Object.fromEntries(FIELDS.map((f) => [f.key, child[f.key] || ""]))
  );
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((f) => {
        payload[f.key] = form[f.key] || null;
      });
      await api.patch(`/children/${child.id}`, payload);
      toast.success("Health & safety information updated");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="health-tab">
      <CardHeader>
        <CardTitle className="text-lg">Relevant health / emergency information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-red-200 bg-red-50 p-4 flex gap-2" data-testid="health-warning">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
          <p className="text-sm text-red-800 font-medium">
            Only record health information necessary for safe delivery of the travel-training service.
            Do not record general medical history. This section is restricted to staff with an operational requirement.
          </p>
        </div>
        <form onSubmit={save} className="space-y-4" data-testid="health-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label>{f.label}</Label>
                <Textarea data-testid={`health-${f.key}`} rows={2} value={form[f.key]} disabled={!isAdmin}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
              </div>
            ))}
          </div>
          {isAdmin && (
            <Button data-testid="health-save-button" type="submit" disabled={saving}
              className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save health information"}
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
